import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, Clock, TrendingUp } from 'lucide-react';
import { SEARCH_SUGGESTIONS, POPULAR_SEARCHES } from '../../data/productsData';

const RECENT_KEY = 'entorid_recent_product_searches';

const loadRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
};

export const ProductSearch = ({ query, onQueryChange }) => {
  const [focused, setFocused] = useState(false);
  const [recent, setRecent] = useState(loadRecent);
  const wrapRef = useRef(null);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setFocused(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const saveRecent = (term) => {
    const t = term.trim();
    if (t.length < 2) return;
    const next = [t, ...recent.filter((r) => r.toLowerCase() !== t.toLowerCase())].slice(0, 5);
    setRecent(next);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  };

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return SEARCH_SUGGESTIONS.filter((s) => s.toLowerCase().includes(q)).slice(0, 6);
  }, [query]);

  const pick = (term) => {
    onQueryChange(term);
    saveRecent(term);
    setFocused(false);
  };

  const showDropdown = focused && (suggestions.length > 0 || (query.trim().length < 2 && recent.length > 0));

  return (
    <div ref={wrapRef} className="relative max-w-2xl mx-auto">
      {/* Input */}
      <div className="relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={(e) => { if (e.key === 'Enter') { saveRecent(query); setFocused(false); } }}
          placeholder="Search termite, mosquito, cockroach, rodent products..."
          className="w-full h-13 sm:h-14 pl-11 pr-11 py-3.5 rounded-full bg-white border-2 border-slate-200 text-sm sm:text-base text-slate-800 placeholder:text-slate-400 shadow-[0_4px_24px_rgba(6,78,59,0.08)] focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-[border-color,box-shadow] duration-200"
          aria-label="Search products"
          data-testid="products-search-input"
        />
        {query && (
          <button
            type="button"
            onClick={() => onQueryChange('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200 transition-colors"
            aria-label="Clear search"
            data-testid="products-search-clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Suggestions / Recent dropdown */}
      {showDropdown && (
        <div
          className="absolute z-40 left-0 right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden suggestion-pop"
          data-testid="products-search-suggestions"
        >
          {suggestions.length > 0 ? (
            suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-slate-700 hover:bg-emerald-50 transition-colors"
              >
                <Search size={14} className="text-slate-400 flex-shrink-0" />
                {s}
              </button>
            ))
          ) : (
            <>
              <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
                <Clock size={12} /> Recent Searches
              </div>
              {recent.map((r) => (
                <button
                  key={r}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-emerald-50 transition-colors"
                >
                  <Clock size={14} className="text-slate-400 flex-shrink-0" />
                  {r}
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {/* Popular searches */}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 mr-1">
          <TrendingUp size={13} /> Popular:
        </span>
        {POPULAR_SEARCHES.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => pick(p)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-[background-color,border-color,color] duration-200 ${
              query.trim().toLowerCase() === p.toLowerCase()
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400 hover:text-emerald-700'
            }`}
            data-testid={`popular-search-${p.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
};
